import { Gavel } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { Checkbox, CircularProgress, FormControlLabel, FormGroup, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { action, computed, extendObservable, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { Fragment } from 'react';
import IconeDespacho from '../../img/icones/IconeDespacho';
import IconeVoto from '../../img/icones/IconeVoto';
import { Minuta, Processo, TipoMinuta } from '../../modelo';
import { MINUTA_ARQUIVADA, MINUTA_ASSINADA, MINUTA_COM_VISTO_ASSINADO, MINUTA_DEVOLVIDA, MINUTA_EM_ELABORACAO, MINUTA_EM_REVISAO, MINUTA_LIBERADA, SituacaoMinuta } from '../../modelo/SituacaoMinuta';
import { DadosProcesso } from '../../modelo/dominio/DadosProcesso';
import { DadosProcessoDto } from '../../modelo/dto/DadosProcessoDto';
import MinutaService from '../../services/MinutaService';
import GabineteStore from '../../store/GabineteStore';
import MensagemStore from '../../store/MensagemStore';
import ProcessoExpandidoStore from '../../store/ProcessoExpandidoStore';
import UsuarioStore from '../../store/UsuarioStore';
import FuncaoSignatarioMinuta from '../FuncaoSignatarioMinuta';
import VersoesMinuta from '../VersoesMinuta';
import DownloadMinuta from '../editor/DownloadMinuta';
import PecaDrawer from '../escaninho/PecaDrawer';


interface IPropsViewMinutas {
    minutas?: boolean;
    processo?: DadosProcessoDto | DadosProcesso;
    espelho?: boolean;
}

const ICONES_MINUTA = new Map([
    [TipoMinuta.DESPACHO, <IconeDespacho />],
    [TipoMinuta.DECISAO, <Gavel />],
    [TipoMinuta.VOTO, <IconeVoto />],
]);

interface TipoMinutaApresentacao {
    label: string;

    checked: boolean;
    filtro: (minuta: Minuta) => boolean

}



class ViewMinutas extends React.Component<IPropsViewMinutas> {
    arquivadas: boolean = false;
    minutasAssinadas = [];
    minutasArquivadas = [];
    minutaPreviewOpened = false;
    minutaPreviewUrl = '';
    carregandoMinutaNumero: number | null = null;

    constructor(props: IPropsViewMinutas) {
        super(props);

        makeObservable(this, {
            arquivadas: observable,
            minutasAssinadas: observable,
            minutasArquivadas: observable,
            minutaPreviewOpened: observable,
            minutaPreviewUrl: observable,
            carregandoMinutaNumero: observable,
            minutas: computed,
            tiposDeMinutas: computed,
            minutasFiltradas: computed,
            toggleCheckTipoMinuta: action,
            checkBoxMinutas: computed,
            fecharPreviewMinuta: action
        });
    }

    componentDidMount(): void {
        // Precisa ser síncrono (não pode retornar Promise): em React 19 + mobx-react 9,
        // um componentDidMount `async` quebra permanentemente a reatividade do observer().
        (async () => {
            const minutasArquivadas = await MinutaService.consultarMinutasArquivadas(this.props.processo.identificadorProcesso);
            const minutasAssinadas = await MinutaService.consultarMinutasAssinadas(this.props.processo.identificadorProcesso);
            runInAction(() => {
                this.minutasArquivadas = [...minutasArquivadas];
                this.minutasAssinadas = [...minutasAssinadas];
            })
        })();
    }

    get minutas() {
        const minutas = [...this.minutasArquivadas, ... this.minutasAssinadas, ...ProcessoExpandidoStore.minutas];
        //tornando únicas, removendo duplicadas
        const minUnicas = [];
        minutas.forEach(min => minUnicas[min.numero] = min);
        return Object.values(minUnicas);
    }

    componentWillUnmount(): void {
        if (this.minutaPreviewUrl) {
            window.URL.revokeObjectURL(this.minutaPreviewUrl);
        }
    }

    filtrarPorSituacao = (situacao) => {
        return this.minutas.filter(minuta => minuta.isSituacao(situacao));
    }

    get tiposDeMinutas(): TipoMinutaApresentacao[] {
        const tipos: TipoMinutaApresentacao[] = [];

        tipos.push(extendObservable({}, {
            label: 'Em elaboração (' + this.filtrarPorSituacao(new MINUTA_EM_ELABORACAO()).length + ')',
            checked: true,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_EM_ELABORACAO())
        }));
        tipos.push(extendObservable({}, {
            label: 'Em revisão (' + this.filtrarPorSituacao(new MINUTA_EM_REVISAO()).length + ')',
            checked: true,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_EM_REVISAO())
        }));
        tipos.push(extendObservable({}, {
            label: 'Liberadas para visto/assinatura (' + this.filtrarPorSituacao(new MINUTA_LIBERADA()).length + ')',
            checked: true,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_LIBERADA())
        }));
        tipos.push(extendObservable({}, {
            label: 'Visto assinado (' + this.filtrarPorSituacao(new MINUTA_COM_VISTO_ASSINADO()).length + ')',
            checked: true,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_COM_VISTO_ASSINADO())
        }));
        tipos.push(extendObservable({}, {
            label: 'Assinadas (' + this.filtrarPorSituacao(new MINUTA_ASSINADA()).length + ')',
            checked: false,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_ASSINADA())
        }));
        tipos.push(extendObservable({}, {
            label: 'Arquivadas (' + this.filtrarPorSituacao(new MINUTA_ARQUIVADA()).length + ')',
            checked: false,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_ARQUIVADA())
        }));
        tipos.push(extendObservable({}, {
            label: 'Devolvidas (' + this.filtrarPorSituacao(new MINUTA_DEVOLVIDA()).length + ')',
            checked: true,
            filtro: (minuta: Minuta) => minuta.isSituacao(new MINUTA_DEVOLVIDA())
        }));

        return tipos;
    }

    get processo() {
        const processo = Processo.novo({ numeroProcesso: this.props.processo.numero });

        processo.classeJudicial = this.props.processo.classeJudicial;
        processo.identificadorProcesso = this.props.processo.identificadorProcesso;
        processo.numero = this.props.processo.numero;

        return processo;
    }

    abrirMinuta = async (minuta: Minuta) => {
        const numeroMinuta = Number(minuta?.numero);
        if (!Number.isFinite(numeroMinuta) || numeroMinuta <= 0) {
            return;
        }

        runInAction(() => {
            this.carregandoMinutaNumero = numeroMinuta;
        });

        try {
            const minutaParaVisualizar = await MinutaService.visualizarMinutaAssinada(numeroMinuta);
            const base64ConteudoEmPdf = (minutaParaVisualizar as any)?.base64ConteudoEmPdf;
            if (!base64ConteudoEmPdf || String(base64ConteudoEmPdf).trim() === '') {
                MensagemStore.apresentarInformacao('Não foi possível recuperar o conteúdo assinado da minuta.');
                return;
            }

            const blobUrl = this.criarBlobUrlPdf(String(base64ConteudoEmPdf));
            if (this.minutaPreviewUrl) {
                window.URL.revokeObjectURL(this.minutaPreviewUrl);
            }
            runInAction(() => {
                this.minutaPreviewUrl = blobUrl;
                this.minutaPreviewOpened = true;
            });
        } catch (error) {
            console.error(error);
            MensagemStore.apresentarErro('Erro ao visualizar minuta. Por favor, tente novamente.');
        } finally {
            runInAction(() => {
                this.carregandoMinutaNumero = null;
            });
        }
    }

    criarBlobUrlPdf = (base64Pdf: string) => {
        const base64Limpo = base64Pdf.replace(/\s/g, '');
        const binario = atob(base64Limpo);
        const tamanho = binario.length;
        const bytes = new Uint8Array(tamanho);
        for (let i = 0; i < tamanho; i++) {
            bytes[i] = binario.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        return window.URL.createObjectURL(blob);
    }

    fecharPreviewMinuta = () => {
        if (this.minutaPreviewUrl) {
            window.URL.revokeObjectURL(this.minutaPreviewUrl);
        }
        this.minutaPreviewUrl = '';
        this.minutaPreviewOpened = false;
    }

    formataTipoMinuta(tipo) {
        return tipo.replace(/DESP/g, "Despacho").replace(/DEC/g, "Decisão");
    }

    renderIconeMinuta(minuta: Minuta) {
        const tipoMinuta = minuta.tipo;
        if (!tipoMinuta) {
            console.error('erro no tipo da minuta', minuta);
            return null;
        }
        return (
            <div style={{ display: 'flex' }}>
                {minuta.isTipoDocumentoMinutaDocx() ?
                    (<DownloadMinuta
                        minutaSelecionada={minuta}
                        processo={this.processo}
                        dadosProcesso={this.props.processo}
                    />)
                    :
                    (<IconButton
                        onClick={() => this.abrirMinuta(minuta)}
                        disabled={this.carregandoMinutaNumero === Number(minuta.numero)}
                    >
                        {ICONES_MINUTA.get(tipoMinuta.codigo)}
                    </IconButton>)
                }
                <span style={{ alignSelf: 'center' }}>
                    {this.formataTipoMinuta(tipoMinuta.descricao)}
                </span>
                {this.props.espelho != true &&
                    <FuncaoSignatarioMinuta minuta={minuta} />
                }
                {minuta.minutaExterna && <Tooltip title="Minuta criada no SAG." style={{ cursor: 'pointer', marginLeft: '4px' }}><InfoIcon color="action" /></Tooltip>}
            </div>
        );
    }

    get minutasFiltradas() {
        let minutas: Minuta[] = [];
        this.tiposDeMinutas.filter(tipo => tipo.checked).forEach(tipo => {
            minutas = minutas.concat(this.minutas.filter(minuta => tipo.filtro(minuta)));
        })
        return [...new Set(minutas)];
    }

    toggleCheckTipoMinuta = (tipo: TipoMinutaApresentacao) => {
        tipo.checked = !tipo.checked;
    };

    get checkBoxMinutas() {
        return (
            <div className='divFlexRow'>
                {this.tiposDeMinutas.map(tipo => {
                    return (
                        <FormGroup key={'view-tipo-minutas-' + tipo.label}>
                            <FormControlLabel
                                control={<Checkbox
                                    sx={{ ml: 2, mr: -1 }}
                                    onChange={this.toggleCheckTipoMinuta.bind(this, tipo)}
                                    checked={tipo.checked}
                                />}
                                label={<span style={{ fontSize: "0.875rem" }}>{tipo.label}</span>}
                            />
                        </FormGroup>
                    )
                })}
            </div>
        );
    }

    renderNomeServidor = (cpf?: string) => {
        if (!cpf) {
            return '-';
        }

        if (GabineteStore.deveAguardarNomeServidor(cpf)) {
            return <CircularProgress size={12} thickness={5} />;
        }

        return GabineteStore.recuperarNomeServidor(cpf);
    }

    render() {
        return (
            <Fragment>
                <h3 style={{ marginLeft: '20px', color: '#01579B', display: this.props.minutas ? '' : 'none' }}>Minutas:</h3>
                <TableContainer style={{ width: '100%', overflow: 'hidden' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={this.minutasFiltradas.some(m => m.codFase) ? 8 : 7} padding={'none'}>{this.checkBoxMinutas}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Tipo de minuta</TableCell>
                                <TableCell>Número da minuta</TableCell>
                                <TableCell>Unidade criadora</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell>Responsável pela criação</TableCell>
                                <TableCell>Responsável pela última alteração</TableCell>
                                {this.minutasFiltradas.some(m => m.codFase) ?
                                    <TableCell> Classe / Recurso interno </TableCell>
                                    :
                                    null
                                }
                                <TableCell>Data da última alteração</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.minutasFiltradas
                                ?.slice()
                                .sort(
                                    (a, b) =>
                                        new Date(b.dataUltimaAlteracao).getTime() -
                                        new Date(a.dataUltimaAlteracao).getTime()
                                )
                                .map((minuta: Minuta) => {
                                    return (
                                        <TableRow key={minuta.numero}>
                                            <TableCell>
                                                {this.renderIconeMinuta(minuta)}
                                            </TableCell>
                                            <TableCell>
                                                {minuta.numero}
                                            </TableCell>
                                            <TableCell>
                                                {minuta.siglaOrgaoJulgador || '-'}
                                            </TableCell>
                                            <TableCell className="descricao-situacao-minuta">
                                                {(() => {
                                                    const ehSecretaria = UsuarioStore.ehSecretario || UsuarioStore.ehLocalizacaoSecretaria;
                                                    const ehDespachoOuDecisao = minuta.tipo?.codigo === TipoMinuta.DESPACHO || minuta.tipo?.codigo === TipoMinuta.DECISAO;
                                                    if (ehSecretaria && ehDespachoOuDecisao && minuta.situacaoMinuta?.codigo === SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA) {
                                                        return "AGUARDANDO ASSINATURA DO RELATOR";
                                                    }
                                                    return minuta.situacaoMinuta?.descricao?.toUpperCase();
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                {this.renderNomeServidor(minuta.cpfResponsavel)}
                                            </TableCell>
                                            <TableCell>
                                                {this.renderNomeServidor(minuta.cpfUltimaAlteracao)}
                                            </TableCell>
                                            {this.minutasFiltradas.some(m => m.codFase) ?
                                                <TableCell>{minuta.codFase}</TableCell>
                                                :
                                                null
                                            }
                                            <TableCell>
                                                {minuta.dataUltimaAlteracao && moment(
                                                    minuta.dataUltimaAlteracao
                                                ).format('DD/MM/YYYY HH:mm')}
                                                <VersoesMinuta minuta={minuta} processo={this.processo} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer >
                <PecaDrawer
                    pecaBodyUrl={this.minutaPreviewUrl}
                    pecaOpened={this.minutaPreviewOpened}
                    closePeca={this.fecharPreviewMinuta}
                    isMinuta={true}
                />
            </Fragment>
        );
    }
}

export default observer(ViewMinutas);
